import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import LacBadge from "./LacBadge";

export default function Navbar() {
  const { pathname } = useLocation();
  const onLanding = pathname === "/";
  const [open, setOpen] = useState(false);

  // Close on route change
  useEffect(() => { setOpen(false); }, [pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Anchor links route to /# when not on landing so we land first then scroll
  const anchor = (hash) => (onLanding ? hash : `/${hash}`);

  const links = [
    { label: "Services", href: anchor("#services") },
    { label: "Gallery", href: "/gallery" },
    { label: "About", href: anchor("#about") },
    { label: "Contact", href: anchor("#contact") },
  ];

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 glass"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <LacBadge size={52} />
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-black text-[13px] tracking-[0.18em] text-chrome">LONDON AUTOMOTIVE</div>
            <div className="font-display font-black text-[13px] tracking-[0.18em] text-chrome">CUSTOMS</div>
          </div>
        </Link>

        {/* Desktop nav (md+) */}
        <nav className="hidden md:flex items-center gap-8" data-testid="nav-desktop">
          {links.map((l) => (
            l.href.startsWith("/") && !l.href.includes("#") ? (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-zinc-300 hover:text-[#D946EF] transition-colors font-medium tracking-wide"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-zinc-300 hover:text-[#D946EF] transition-colors font-medium tracking-wide"
              >
                {l.label}
              </a>
            )
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/booking"
            data-testid="nav-book-now-btn"
            className="px-5 py-2.5 rounded-full bg-[#D946EF] text-white font-semibold text-sm hover:bg-[#F0ABFC] transition-all hover:shadow-[0_0_30px_rgba(217,70,239,0.55)]"
          >
            Book Now
          </Link>

          {/* Hamburger trigger — mobile only */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/15 text-white hover:border-[#D946EF]/60 hover:text-[#D946EF] transition"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <div
        data-testid="nav-mobile-panel"
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${open ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"}`}
      >
        <nav className="px-6 pt-2 pb-6 border-t border-white/5 bg-[#050505]/95 backdrop-blur-md flex flex-col">
          {links.map((l, i) => {
            const isRoute = l.href.startsWith("/") && !l.href.includes("#");
            const cls = "py-4 text-base text-zinc-200 hover:text-[#D946EF] transition border-b border-white/5 font-medium tracking-wide";
            return isRoute ? (
              <Link
                key={l.href}
                to={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={cls}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={cls}
              >
                {l.label}
              </a>
            );
          })}
          <Link
            to="/gallery/submit"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-link-submit-pov"
            className="py-4 text-base text-[#D946EF] hover:text-[#F0ABFC] transition border-b border-white/5 font-semibold tracking-wide"
          >
            Share your POV →
          </Link>
          <Link
            to="/booking"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-book-now"
            className="mt-5 text-center px-5 py-3 rounded-full bg-[#D946EF] text-white font-semibold text-sm hover:bg-[#F0ABFC] transition"
          >
            Book Now
          </Link>
        </nav>
      </div>
    </header>
  );
}
